import type { Messages } from "./en";

/** Operator dashboard (/ops). Merged into de.ts under "ops". */
export const opsDe: Messages["ops"] = {
  badge: "Ops",
  nav: {
    overview: "Übersicht",
    organizations: "Organisationen",
    partners: "IT-Partner",
    users: "Personen",
    delivery: "Verteilung",
    graph: "Microsoft Graph",
    emails: "E-Mails",
    operators: "Betreiber",
    audit: "Protokoll",
    account: "Mein Konto",
    system: "System",
    settings: "Einstellungen",
    bugReports: "Problemmeldungen",
    leads: "Anfragen",
    events: "Ereignisse",
  },
  partners: {
    title: "IT-Partner",
    description:
      "Partnerfirmen, die Kunden über das Partnerportal betreuen: ihre Kunden, was sie bezahlen und wer sich anmeldet. Öffnen Sie einen Partner für Details, Anträge auf Rechnung und Sperren.",
    empty: "Noch kein IT-Partner registriert.",
    name: "Partner",
    code: "Code",
    customers: "Kunden",
    users: "Konten",
    created: "Registriert",
    suspended: "Gesperrt",
    suspend: "Sperren",
    resume: "Entsperren",
    suspendTitle: "{{name}} sperren?",
    suspendText:
      "Alle Partnerkonten werden abgemeldet und verlieren den Zugriff auf alle Kunden. Die Kunden behalten ihre Daten. Kunden, die über den Partner laufen, bezahlen nach der bezahlten Periode selbst; Entsperren stellt das nicht zurück.",
    suspendedToast: "Partner gesperrt.",
    resumed: "Partner entsperrt.",
    statPartners: "Partner",
    statSuspended_one: "{{count}} gesperrt",
    statSuspended_other: "{{count}} gesperrt",
    statCustomers: "Verknüpfte Kunden",
    statBilled_one: "{{count}} über einen Partner abgerechnet",
    statBilled_other: "{{count}} über Partner abgerechnet",
    statMonthly: "Monatlich über Partner",
    statMonthlyHint: "bei der heutigen Anzahl Personen, ohne MWST",
    statOpen: "Offene Anträge auf Rechnung",
    statAttention_one: "{{count}} Kunde braucht Aufmerksamkeit",
    statAttention_other: "{{count}} Kunden brauchen Aufmerksamkeit",
    search: "Name oder Code suchen",
    monthly: "Pro Monat",
    attention: "Brauchen Aufmerksamkeit",
    lastLogin: "Letzte Anmeldung",
    never: "Nie",
    noPayment: "Keine Zahlungsmethode",
    payment: "Zahlung",
    subscriptionLabel: "Stripe-Abo",
    subscription: {
      active: "Aktiv",
      trialing: "Testphase",
      past_due: "Zahlung fehlgeschlagen",
      unpaid: "Unbezahlt",
      canceled: "Beendet",
      incomplete: "Unvollständig",
      incomplete_expired: "Abgelaufen",
      paused: "Pausiert",
    },
    until: "bis {{date}}",
    noSubscription: "Noch keines (entsteht, sobald der erste Kunde fällig ist)",
    usersText_one: "{{count}} Konto",
    usersText_other: "{{count}} Konten",
    person: "Person",
    secondFactor: "Zweiter Faktor",
    totp: "Authenticator-App",
    passkeys_one: "{{count}} Passkey",
    passkeys_other: "{{count}} Passkeys",
    noSecondFactor: "Keiner",
    invited: "Eingeladen",
    invitedUntil: "Einladung gültig bis {{date}}",
    noCustomers: "Noch keine Kunden.",
    billingLabel: "Abrechnung",
    paysPartner: "Partner bezahlt",
    paysCustomer: "Kunde bezahlt",
    partnerUntil: "Partner bezahlt bis {{date}}",
    link: { pending: "Wartet auf Zustimmung", unlinked: "Verknüpfung beendet" },
    reports: "Monatsberichte",
    reportCustomers_one: "{{count}} Kunde",
    reportCustomers_other: "{{count}} Kunden",
    billed: "Über Partner abgerechnet",
    invoiceRequested: "Rechnung beantragt",
    invoiceApproved: "Zahlt auf Rechnung",
    card: "Karte",
    pastDue: "Zahlung fehlgeschlagen",
    review: "Prüfen",
    reviewTitle: "Zahlung auf Rechnung für {{name}}",
    reviewText:
      "Der Partner zahlt monatlich auf Rechnung, zahlbar innert 10 Tagen, statt mit Karte. Geben Sie nur Partner frei, denen Sie Kredit gewähren.",
    invoiceEmail: "E-Mail für Rechnungen",
    invoiceAddress: "Rechnungsadresse",
    approve: "Freigeben",
    reject: "Ablehnen",
    approved: "Zahlung auf Rechnung freigegeben.",
    rejected: "Antrag abgelehnt.",
  },
  elevate: {
    title: "Bestätigen Sie, dass Sie es sind",
    description:
      "Geben Sie den aktuellen Code aus Ihrer Authenticator-App ein. Danach funktionieren heikle Aktionen 10 Minuten lang.",
    confirm: "Bestätigen",
    zuvoDescription:
      "Melden Sie sich bei zuvo neu an. Danach funktionieren heikle Aktionen 10 Minuten lang; führen Sie die Aktion anschliessend noch einmal aus.",
    zuvoButton: "Mit zuvo bestätigen",
  },
  login: {
    title: "Kontaktfix-Betrieb",
    codeSubtitle: "Geben Sie den Code aus Ihrer Authenticator-App ein.",
    password: "Passwort",
    code: "Bestätigungscode",
    recoveryCode: "Wiederherstellungscode",
    recoveryHint: "Jeder Wiederherstellungscode funktioniert einmal.",
    signIn: "Anmelden",
    useRecovery: "Wiederherstellungscode verwenden",
    useApp: "Authenticator-App verwenden",
    breakGlass: "Notfall-Anmeldung",
    breakGlassHint: "Nur solange die zuvo-Anmeldung nicht verfügbar ist. Jede Anmeldung wird protokolliert.",
    notConfigured: "Die Anmeldung für den Betrieb ist nicht eingerichtet.",
    errors: {
      zuvo_no_access: "Sie haben keinen Zugriff auf Kontaktfix. Den Zugriff vergibt der Zuvo Hub.",
      zuvo_mfa: "Die Anmeldung bei zuvo braucht einen zweiten Faktor (Passkey).",
      zuvo_stale: "Ihre Anmeldung bei zuvo ist zu alt. Bitte melden Sie sich neu an.",
      zuvo_invalid: "Die Anmeldung bei zuvo war unvollständig. Bitte versuchen Sie es erneut.",
      zuvo_disabled: "Ihr Betreiberkonto ist in Kontaktfix deaktiviert.",
      zuvo_failed: "Die Anmeldung bei zuvo hat nicht geklappt. Bitte versuchen Sie es erneut.",
      zuvo_unavailable: "Die zuvo-Anmeldung ist gerade nicht erreichbar.",
    },
  },
  setup: {
    title: "Willkommen, {{name}}",
    resetTitle: "Zugang neu einrichten, {{name}}",
    validUntil: "Dieser Link gilt bis {{date}}.",
    stepOf: "Schritt {{current}} von {{total}}",
    steps: { password: "Passwort", authenticator: "Authenticator", codes: "Wiederherstellungscodes" },
    scan: "Scannen Sie den QR-Code mit Ihrer Authenticator-App (1Password, Microsoft Authenticator, Google Authenticator und andere).",
    qrAlt: "QR-Code für die Authenticator-App",
    manual: "Scannen geht nicht? Schlüssel von Hand eingeben",
    enterCode: "Code aus der App",
    verify: "Prüfen und abschliessen",
    finish: "Zum Dashboard",
    invalidTitle: "Dieser Link funktioniert nicht mehr",
    invalidText:
      "Einrichtungslinks funktionieren einmal und laufen nach 72 Stunden ab. Bitten Sie einen anderen Betreiber um einen neuen Link.",
  },
  password: {
    new: "Neues Passwort",
    confirm: "Passwort wiederholen",
    hint: "Mindestens 12 Zeichen. Am besten eine Passphrase aus dem Passwort-Manager.",
    tooShort: "Verwenden Sie mindestens 12 Zeichen.",
    mismatch: "Die Passwörter stimmen nicht überein.",
  },
  recovery: {
    explain:
      "Wenn Sie Ihr Telefon verlieren, meldet Sie jeder dieser Codes einmal anstelle der Authenticator-App an. Speichern Sie sie in Ihrem Passwort-Manager. Sie werden nur jetzt angezeigt.",
    copy: "Codes kopieren",
    download: "Herunterladen",
    saved: "Ich habe die Codes gespeichert",
    fileHeader: "Kontaktfix-Betrieb: Wiederherstellungscodes für {{email}}. Jeder Code funktioniert einmal.",
  },
  overview: {
    title: "Übersicht",
    description: "Die ganze Plattform auf einen Blick.",
    maintenanceOn: "Wartungsmodus ist eingeschaltet: Kunden sehen die Wartungsseite.",
    mrr: "Monatlich wiederkehrender Umsatz",
    mrrHint: "Aktive und überfällige Abonnemente, heutige Anzahl Personen",
    subscriptions: "Abonnemente",
    subscriptionsHint: "{{trialing}} in der Testphase · {{pastDue}} Zahlung überfällig",
    leads: "Anfragen",
    last30Days: "Letzte 30 Tage",
    events: "Ereignisse",
    eventsHint: "Letzte 30 Tage · {{count}} warten auf den Zuvo Hub",
    acquisition: "Woher Organisationen kommen",
    acquisitionHint: "UTM-Parameter des Anmeldelinks, letzte 90 Tage. Ohne Cookies.",
    source: "Quelle / Medium",
    campaign: "Kampagne",
    subscribed: "Abonniert",
    direct: "Direkt oder unbekannt",
    noSignups: "Keine neuen Organisationen in den letzten 90 Tagen.",
    orgs: "Organisationen",
    orgsHint: "{{connected}} verteilen · {{suspended}} gesperrt",
    users: "Personen mit Anmeldung",
    usersHint: "{{count}} aktiv in den letzten 30 Tagen",
    contacts: "Kontakte",
    recipients: "Empfangende Postfächer",
    recipientsHint_one: "{{count}} braucht Aufmerksamkeit",
    recipientsHint_other: "{{count}} brauchen Aufmerksamkeit",
    runs: "Synchronisationen, 24 Stunden",
    runsHint: "{{failed}} fehlgeschlagen · {{errors}} mit Fehlern",
    emails: "E-Mails, 7 Tage",
    emailsHint: "{{count}} nicht zugestellt",
    attentionTitle: "Braucht Aufmerksamkeit",
    allGood: "Alles in Ordnung.",
    attention: {
      connection_error: "Verbindung zu Microsoft 365 unterbrochen",
      certificate_expiring: "Zertifikat läuft am {{date}} ab",
      sync_failed: "Synchronisation fehlgeschlagen",
      recipient_problems_one: "{{count}} Postfach nicht erreichbar",
      recipient_problems_other: "{{count}} Postfächer nicht erreichbar",
    },
    signups: "Neue Organisationen",
    signupsDescription: "{{count}} in den letzten 30 Tagen",
    signupsTotal: "in 12 Wochen",
    weekOf: "Woche ab {{date}}",
    newOrgs_one: "{{count}} neue Organisation",
    newOrgs_other: "{{count}} neue Organisationen",
    thisWeek: "Diese Woche",
  },
  orgs: {
    title: "Organisationen",
    description: "Alle Kunden. Öffnen Sie eine für Details, Support-Zugriff und Aktionen.",
    search: "Nach Name oder Tenant-ID suchen",
    empty: "Keine Organisationen gefunden.",
    suspended: "Gesperrt",
    columns: {
      name: "Organisation",
      connection: "Microsoft 365",
      contacts: "Kontakte",
      receiving: "Empfänger",
      problems: "Probleme",
      members: "Mitglieder",
      lastSync: "Letzte Verteilung",
      created: "Erstellt",
    },
  },
  org: {
    limits: {
      title: "Kontakt-Limits",
      perRecipient: "Kontakte pro Person",
      perOrganization: "Kontakte in der Organisation",
      default: "{{value}} (Standard)",
      hint: "Leer lassen für den Standard. Höhere Limits nur nach Rücksprache: Handys werden mit sehr vielen Kontakten langsam, und die erste Verteilung dauert länger.",
      saved: "Limits gespeichert.",
    },
    billing: {
      title: "Abrechnung",
      users: "Personen und Total pro Monat",
      trial: "Testphase",
      trialRange: "{{from}} bis {{to}}",
      trialNotStarted: "Beginnt mit der ersten Verteilung",
      subscription: "Stripe-Abo",
      cancelAtPeriodEnd: "endet mit der Periode",
      customer: "Stripe-Kunde",
      extend_one: "Testphase um {{count}} Tag verlängern",
      extend_other: "Testphase um {{count}} Tage verlängern",
      extended:
        "Testphase verlängert. War die Verteilung pausiert, läuft jetzt eine vollständige Synchronisation.",
    },
    support: "Als Support öffnen",
    sync: "Änderungen synchronisieren",
    fullSync: "Vollständig synchronisieren (alle Postfächer prüfen)",
    testConnection: "Verbindung testen",
    connectionOk: "Die Verbindung funktioniert.",
    connectionFailed: "Verbindung fehlgeschlagen: {{error}}",
    suspend: "Sperren",
    resume: "Entsperren",
    resumed: "Organisation entsperrt. Eine vollständige Synchronisation läuft.",
    suspended: "Organisation gesperrt.",
    suspendedSince: "Gesperrt seit {{date}}",
    suspendTitle: "{{name}} sperren?",
    suspendText:
      "Mitglieder können nicht mehr in Kontaktfix arbeiten und es wird nichts verteilt. Kontakte in den Postfächern bleiben. Sie können jederzeit entsperren.",
    reason: "Grund",
    reasonHint: "Intern. Nur für Betreiber sichtbar.",
    delete: "Organisation löschen",
    deleted: "Organisation gelöscht.",
    deleteTitle: "{{name}} löschen?",
    deleteText:
      "Damit werden {{count}} Kontakte, alle Listen, Mitglieder und der Verlauf endgültig gelöscht. Bereits verteilte Kontakte bleiben in den Postfächern: Um sie zu entfernen, zuerst die Listenzuweisungen aufheben und synchronisieren lassen.",
    typeName: "Geben Sie {{name}} zur Bestätigung ein",
    editTitle: "Organisation bearbeiten",
    languageHint: "Sprache der E-Mails und neuer Standardwerte.",
    details: "Details",
    tenantId: "Tenant-ID",
    language: "Sprache",
    delivery: "Verteilung",
    deliveryModes: { folder: "Eigener Ordner", main: "Hauptordner Kontakte" },
    connectionMethod: "Verbunden über",
    methods: { consent: "Kontaktfix-App (Zustimmung)", own_app: "Eigene App-Registrierung" },
    connection: "Verbindung",
    checked: "geprüft {{when}}",
    clientId: "Anwendungs-ID",
    notVerified: "Noch nicht geprüft",
    certificate: "Zertifikat",
    certificateExpires: "Läuft ab am {{date}} ({{days}} Tage)",
    credential: "Anmeldeinformation",
    secret: "Geheimer Clientschlüssel",
    created: "Erstellt",
    recipients: "Postfächer",
    lists: "Listen",
    assignments_one: "{{count}} Zuweisung",
    assignments_other: "{{count}} Zuweisungen",
    tabs: {
      overview: "Übersicht",
      members: "Mitglieder",
      delivery: "Verteilung",
      activity: "Aktivität",
      emails: "E-Mails",
    },
  },
  users: {
    title: "Personen",
    description: "Alle, die sich mit Microsoft in der Kunden-App angemeldet haben.",
    search: "Nach Name oder E-Mail suchen",
    empty: "Niemand gefunden.",
    noOrg: "Keine Organisation",
    signOutHint: "Überall abmelden",
    signedOut_one: "{{count}} Sitzung beendet.",
    signedOut_other: "{{count}} Sitzungen beendet.",
    columns: {
      orgs: "Organisationen",
      lastLogin: "Letzte Anmeldung",
      firstLogin: "Erste Anmeldung",
      sessions: "Sitzungen",
      added: "Hinzugefügt",
    },
  },
  delivery: {
    title: "Verteilung",
    description:
      "Warteschlangen, letzte Synchronisationen und Postfächer mit Problemen über alle Organisationen.",
    active: "aktiv",
    queued: "wartend",
    deferred: "{{count}} geplant",
    failedJobs: "{{count}} fehlgeschlagen",
    problems: "Postfächer mit Problemen",
    problemsHint: "Neueste zuerst, höchstens 200.",
    person: "Person",
    lastAttempt: "Letzter Versuch",
    failed: "Fehlgeschlagen",
  },
  graph: {
    title: "Microsoft Graph",
    description:
      "Jede Anfrage an Microsoft Graph, live. Gedrosselt heisst: Microsoft hat mit 429, 503 oder 504 geantwortet, auch einzelne Anfragen in einem Batch. Gespeichert 30 Tage.",
    pause: "Anhalten",
    resume: "Live",
    live: "Live, alle 3 Sekunden",
    export: "CSV exportieren",
    window: "Zeitraum",
    windows: {
      15: "Letzte 15 Minuten",
      60: "Letzte Stunde",
      360: "Letzte 6 Stunden",
      1440: "Letzte 24 Stunden",
    },
    allOrgs: "Alle Organisationen",
    requests: "Anfragen",
    throttled: "Gedrosselt",
    errors: "Fehler",
    avg: "Mittlere Dauer",
    maxRetryAfter: "Längstes Retry-After",
    none: "keines",
    perMinute: "Anfragen pro Minute",
    perMinuteHint: "Orange: gedrosselte Anfragen.",
    chartLabel: "Anfragen pro Minute, gedrosselte Anfragen hervorgehoben",
    tooltip: "{{requests}} Anfragen, {{throttled}} gedrosselt, {{errors}} Fehler",
    byOrg: "Nach Organisation",
    byResource: "Nach Endpunkt",
    lastThrottled: "Zuletzt gedrosselt um {{time}}",
    resource: "Endpunkt",
    log: "Anfragen",
    logHint: "Neueste zuerst, höchstens 200. Für ältere Anfragen den CSV-Export verwenden.",
    filters: { all: "Alle", throttled: "Gedrosselt", errors: "Fehler" },
    empty: "Keine Anfragen in diesem Zeitraum.",
    time: "Zeit",
    duration: "Dauer",
    attempt: "Versuch",
    attemptShort: "Versuch {{n}}",
    batch: "Batch",
    batchShort: "{{size}} ({{throttled}} gedrosselt)",
    batchDetail: "{{size}} Anfragen, {{throttled}} gedrosselt, {{failed}} fehlgeschlagen",
    retryAfter: "Retry-After",
    mailbox: "Postfach",
    path: "Pfad",
    error: "Fehler",
    requestId: "Request-ID",
    headers: "Drosselungs-Header",
    network: "Netzwerk",
  },
  emails: {
    title: "E-Mails",
    description: "Benachrichtigungen, verschickt mit React Email und Resend, und ihr Zustellstatus.",
    setup: "Versand",
    sending: "Resend",
    resendOn: "Aktiv",
    resendOff: "Kein Versand: RESEND_API_KEY fehlt, E-Mails werden nur protokolliert",
    from: "Absender",
    replyTo: "Antwort an",
    webhook: "Zustellstatus",
    webhookOn: "Resend-Webhook aktiv",
    webhookOff: "Aus (RESEND_WEBHOOK_SECRET nicht gesetzt)",
    templates: "Vorlagen",
    templatesHint: "Vorschau mit Beispieldaten.",
    preview: "Vorschau",
    test: "Test-E-Mail senden",
    send: "Senden",
    recipient: "Empfänger",
    testResult: {
      sent: "Test-E-Mail gesendet.",
      logged: "Nur protokolliert: Der Versand ist nicht eingerichtet.",
    },
    testFailed: "Versand fehlgeschlagen: {{error}}",
    log: "Protokoll",
    search: "Empfänger oder Betreff suchen",
    allStatuses: "Alle Status",
    empty: "Keine E-Mails.",
    platform: "Plattform",
    columns: { sent: "Gesendet", to: "An", subject: "Betreff" },
    status: {
      sending: "Wird gesendet",
      sent: "Gesendet",
      logged: "Nur protokolliert",
      failed: "Fehlgeschlagen",
      delivered: "Zugestellt",
      delayed: "Verzögert",
      bounced: "Unzustellbar",
      complained: "Als Spam markiert",
      suppressed: "Unterdrückt",
    },
  },
  admins: {
    title: "Betreiber",
    description:
      "Mitarbeitende von Kontaktfix mit Zugang zu diesem Dashboard. Alle haben ein eigenes Passwort und eine eigene Authenticator-App.",
    invite: "Betreiber einladen",
    inviteTitle: "Betreiber einladen",
    inviteText:
      "Die Person erhält einen Link, um ein Passwort festzulegen und eine Authenticator-App zu verbinden.",
    sendInvite: "Einladung senden",
    emailLanguage: "Sprache der E-Mail",
    linkSent: "Einrichtungslink per E-Mail gesendet. Er funktioniert einmal, bis {{date}}.",
    linkWarning: "Geben Sie diesen Link nur dieser Person weiter, über einen vertrauenswürdigen Kanal.",
    linkValid: "Link gültig bis {{date}}",
    resetDone: "Neuer Einrichtungslink",
    you: "Sie",
    locked: "Gesperrt",
    status: { invited: "Eingeladen", active: "Aktiv", disabled: "Deaktiviert" },
    recoveryCodes: "Wiederherstellungscodes",
    codesLeft_one: "{{count}} übrig",
    codesLeft_other: "{{count}} übrig",
    resend: "Neuen Link senden",
    reset: "Zugang zurücksetzen",
    unlock: "Entsperren",
    enable: "Aktivieren",
    disable: "Deaktivieren",
    securityNote:
      "Heikle Aktionen (Betreiber, Support-Zugriff, Organisationen sperren oder löschen) verlangen einen frischen Code aus der Authenticator-App. Alles, was Betreiber tun, steht im Protokoll.",
    done: {
      disable: "{{name}} ist deaktiviert.",
      enable: "{{name}} ist aktiviert.",
      unlock: "{{name}} ist entsperrt.",
      delete: "{{name}} ist gelöscht.",
    },
    confirm: {
      reset: {
        title: "Zugang von {{name}} zurücksetzen?",
        text: "Passwort, Authenticator und Wiederherstellungscodes werden entfernt und alle Sitzungen beendet. {{name}} erhält einen neuen Einrichtungslink.",
        button: "Zurücksetzen und Link senden",
      },
      disable: {
        title: "{{name}} deaktivieren?",
        text: "Alle Sitzungen enden, auch offene Support-Sitzungen. Sie können das Konto später wieder aktivieren.",
        button: "Deaktivieren",
      },
      delete: {
        title: "{{name}} löschen?",
        text: "Das Konto wird endgültig entfernt. Die Einträge im Protokoll bleiben.",
        button: "Löschen",
      },
    },
  },
  audit: {
    title: "Protokoll",
    description: "Alles, was Betreiber getan haben: Anmeldungen, Support-Zugriffe und Änderungen.",
    when: "Wann",
    who: "Betreiber",
    what: "Aktion",
    target: "Ziel",
    ip: "IP-Adresse",
    actions: {
      auth: {
        login: "Angemeldet",
        logout: "Abgemeldet",
        password_failed: "Falsches Passwort",
        code_failed: "Falscher Code",
        break_glass: "Notfall-Anmeldung verwendet",
      },
      account: {
        setup_completed: "Konto eingerichtet",
        password_changed: "Passwort geändert",
        recovery_codes_regenerated: "Neue Wiederherstellungscodes erstellt",
      },
      admin: {
        invited: "Betreiber eingeladen",
        reset: "Zugang eines Betreibers zurückgesetzt",
        disabled: "Betreiber deaktiviert",
        enabled: "Betreiber aktiviert",
        unlocked: "Betreiber entsperrt",
        deleted: "Betreiber gelöscht",
      },
      org: {
        updated: "Organisation bearbeitet",
        sync_requested: "Synchronisation gestartet",
        sync_cancelled: "Synchronisation gestoppt",
        connection_tested: "Verbindung getestet",
        suspended: "Organisation gesperrt",
        resumed: "Organisation entsperrt",
        deleted: "Organisation gelöscht",
        support_access_started: "Support-Zugriff gestartet",
        support_access_ended: "Support-Zugriff beendet",
      },
      partner: {
        suspended: "IT-Partner gesperrt",
        resumed: "IT-Partner entsperrt",
        invoice_approved: "Zahlung auf Rechnung freigegeben",
        invoice_rejected: "Zahlung auf Rechnung abgelehnt",
      },
      user: { signed_out: "Person überall abgemeldet" },
      email: { test_sent: "Test-E-Mail gesendet" },
      settings: {
        maintenance_on: "Wartungsmodus eingeschaltet",
        maintenance_off: "Wartungsmodus ausgeschaltet",
      },
    },
  },
  account: {
    title: "Mein Konto",
    description: "Passwort, Authenticator und Wiederherstellungscodes.",
    profile: "Profil",
    twoFactor: "Zwei-Faktor-Authentifizierung",
    twoFactorOn: "Aktiv (Authenticator-App)",
    twoFactorZuvo: "Aktiv (zuvo-Anmeldung mit Passkey)",
    zuvoManaged: "Sie melden sich mit zuvo an. Passkey und Zugriff verwaltet der Zuvo Hub.",
    password: "Passwort",
    passwordHint: "Beim Ändern werden Ihre anderen Sitzungen abgemeldet.",
    currentPassword: "Aktuelles Passwort",
    changePassword: "Passwort ändern",
    passwordChanged: "Passwort geändert. Ihre anderen Sitzungen wurden abgemeldet.",
    recovery: "Wiederherstellungscodes",
    recoveryHint_one: "{{count}} unbenutzter Code übrig.",
    recoveryHint_other: "{{count}} unbenutzte Codes übrig.",
    regenerate: "Neue Codes erstellen",
    newCodes: "Neue Wiederherstellungscodes",
    lostPhone:
      "Neues Telefon oder Authenticator-App verloren? Bitten Sie einen anderen Betreiber, Ihren Zugang zurückzusetzen; danach richten Sie Passwort und App neu ein.",
  },
  system: {
    title: "System",
    description: "Zustand der Plattform, Integrationen und Hintergrundjobs.",
    status: "Zustand",
    environment: "Umgebung",
    version: "Version",
    noVersion: "Keine (nicht aus einem Deployment)",
    database: "Datenbank",
    dbOk: "Erreichbar ({{ms}} ms)",
    dbDown: "Nicht erreichbar",
    maintenance: "Betrieb",
    microsoft: "Microsoft-Anmeldung",
    email: "E-Mail-Versand",
    emailWebhook: "E-Mail-Zustellstatus",
    billing: "Abrechnung (Stripe)",
    backups: "Backups",
    backupsHint: "Werden vom zuvo-Team ausserhalb der App betrieben und geprüft.",
    on: "Ein",
    off: "Aus",
    jobs: "Hintergrundjobs",
    queue: "{{active}} aktiv, {{queued}} wartend",
  },
  events: {
    title: "Ereignisse",
    description:
      "Die zuvo-Standardereignisse, die Kontaktfix aufgezeichnet hat. Sie gehen an den Zuvo Hub, sobald er verbunden ist.",
    filter: "Nach Ereignis filtern",
    all: "Alle Ereignisse",
    empty: "Noch keine Ereignisse.",
    when: "Wann",
    name: "Ereignis",
    properties: "Details",
    hub: "Zuvo Hub",
    delivered: "Gesendet",
    waiting: "Wartend",
  },
  leads: {
    title: "Anfragen",
    description:
      "Kontakt- und Wartelistenformulare der Website. Jeder Betreiber erhält zu jeder Anfrage eine E-Mail.",
    empty: "Noch keine Anfragen.",
    when: "Eingegangen",
    who: "Von",
    message: "Nachricht",
    kind: "Formular",
    kinds: { contact: "Kontakt", waitlist: "Warteliste" },
  },
  bugReports: {
    title: "Problemmeldungen",
    description:
      "Meldungen aus «Problem melden» in der App und hier. Jede wird zu einem GitHub-Issue im Produkt-Repository.",
    empty: "Noch keine Meldungen.",
    detail: "Problemmeldung",
    when: "Gemeldet",
    org: "Organisation",
    fromOps: "Betriebs-Dashboard",
    page: "Seite",
    browser: "Browser",
    release: "Version",
    errors: "Letzte Fehler",
    issue: "GitHub-Issue",
    issueCreated: "Erstellt",
    issueFailed: "Fehlgeschlagen",
    noIssue: "Noch nicht (GITHUB_ISSUES_TOKEN fehlt oder ausstehend)",
  },
  settings: {
    title: "Einstellungen",
    description: "Einstellungen für die ganze Plattform.",
    maintenance: "Wartungsmodus",
    maintenanceHint:
      "Kunden sehen eine Wartungsseite und die API antwortet mit 503. Betreiber und Support-Sitzungen arbeiten weiter, die Verteilung im Hintergrund läuft weiter.",
    maintenanceOn: "Wartungsmodus ist eingeschaltet.",
    maintenanceOff: "Wartungsmodus ist ausgeschaltet.",
    activeSince: "Eingeschaltet seit {{date}} von {{name}}.",
    inactive: "Ausgeschaltet. Kontaktfix ist für alle Kunden erreichbar.",
  },
};
